// ============================================================
//  src/admin/pages/user-stats/AdminUserStatsPage.js
// ============================================================
import React, { useEffect, useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getReferralContest, getTopReferrals, getTopFunding,
  getTopWalletUsers, getSalesAnalysis,
} from '../../api/adminClient';
import { SectionHeader, AdminCard, AdminSearch } from '../../components/AdminUI';

// ── helpers ────────────────────────────────────────────────
function fmt(n) {
  return Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

// ── generic table — passes (row, index) to render ──────────
function StatsTable({ columns, rows, loading, emptyMsg = 'No data found.' }) {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-light)' }}>
        <span className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-light)', fontSize: 14 }}>
        {emptyMsg}
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            {columns.map((c) => (
              <th key={c.key} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {columns.map((c) => (
                <td key={c.key} style={{ padding: '10px 10px', ...(c.style || {}) }}>
                  {/* FIX: pass both row AND index to render */}
                  {c.render ? c.render(row, i) : row[c.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────
function Section({ title, icon, color, children }) {
  return (
    <AdminCard style={{ marginBottom: 24 }}>
      <h3 style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-dark)', marginBottom: 16 }}>
        <i className={`fas ${icon}`} style={{ color, marginRight: 8 }} />
        {title}
      </h3>
      {children}
    </AdminCard>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function AdminUserStatsPage() {

  const [contest,       setContest]       = useState([]);
  const [contestLoad,   setContestLoad]   = useState(true);
  const [contestSearch, setContestSearch] = useState('');

  const [referFund,     setReferFund]     = useState([]);
  const [referLoad,     setReferLoad]     = useState(true);
  const [referSearch,   setReferSearch]   = useState('');

  const [topUsers,      setTopUsers]      = useState([]);
  const [topUsersLoad,  setTopUsersLoad]  = useState(true);
  const [topUsersSearch,setTopUsersSearch]= useState('');

  const [wallet,        setWallet]        = useState([]);
  const [walletLoad,    setWalletLoad]    = useState(true);
  const [walletSearch,  setWalletSearch]  = useState('');

  // Sales — keep full dataset in a ref so search always filters against it
  const [sales,         setSales]         = useState([]);
  const allSalesRef                        = useRef([]);
  const [salesLoad,     setSalesLoad]     = useState(true);
  const [salesSearch,   setSalesSearch]   = useState('');
  const [dateFrom,      setDateFrom]      = useState(today());
  const [dateTo,        setDateTo]        = useState(today());

  // ── loaders ──────────────────────────────────────────────
  const loadContest = useCallback(async () => {
    setContestLoad(true);
    try {
      const r = await getReferralContest(10000);
      setContest(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch { toast.error('Failed to load referral contest'); }
    finally { setContestLoad(false); }
  }, []);

  const loadReferFund = useCallback(async () => {
    setReferLoad(true);
    try {
      const r = await getTopReferrals(10000);
      setReferFund(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch { toast.error('Failed to load refer fund'); }
    finally { setReferLoad(false); }
  }, []);

  const loadTopUsers = useCallback(async () => {
    setTopUsersLoad(true);
    try {
      const r = await getTopFunding(10);
      setTopUsers(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch { toast.error('Failed to load top users'); }
    finally { setTopUsersLoad(false); }
  }, []);

  const loadWallet = useCallback(async () => {
    setWalletLoad(true);
    try {
      const r = await getTopWalletUsers(10);
      setWallet(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch { toast.error('Failed to load top wallet users'); }
    finally { setWalletLoad(false); }
  }, []);

  const loadSales = useCallback(async (from, to) => {
    setSalesLoad(true);
    setSalesSearch(''); // clear search on new date fetch
    try {
      const r = await getSalesAnalysis({ limit: 1000, datefrom: from, dateto: to });
      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      allSalesRef.current = data;
      setSales(data);
    } catch { toast.error('Failed to load sales data'); }
    finally { setSalesLoad(false); }
  }, []);

  useEffect(() => {
    loadContest();
    loadReferFund();
    loadTopUsers();
    loadWallet();
    loadSales(today(), today());
  }, [loadContest, loadReferFund, loadTopUsers, loadWallet, loadSales]);

  // ── sales search — filter against full dataset in ref ────
  const handleSalesSearch = (e) => {
    const q = e.target.value;
    setSalesSearch(q);
    if (!q) {
      setSales(allSalesRef.current);
    } else {
      const lq = q.toLowerCase();
      setSales(
        allSalesRef.current.filter((r) =>
          r.email?.toLowerCase().includes(lq) ||
          (`${r.fname} ${r.lname}`).toLowerCase().includes(lq)
        )
      );
    }
  };

  // ── filter helpers for other tables ──────────────────────
  const filterByEmail = (list, q) => {
    if (!q) return list;
    const lq = q.toLowerCase();
    return list.filter((r) =>
      r.email?.toLowerCase().includes(lq) ||
      r.referrals?.toLowerCase().includes(lq)
    );
  };
  const filterByName = (list, q) => {
    if (!q) return list;
    const lq = q.toLowerCase();
    return list.filter((r) =>
      r.email?.toLowerCase().includes(lq) ||
      (`${r.fname} ${r.lname}`).toLowerCase().includes(lq)
    );
  };

  // ── column definitions ────────────────────────────────────
  // FIX: render receives (row, index) — use index for # column
  const referralCols = [
    { key: '#',           label: '#',           render: (_r, i) => i + 1 },
    { key: 'email',       label: 'Email' },
    { key: 'total_earned',label: 'Amount',       render: (r) => `₦${fmt(r.total_earned)}` },
    { key: 'refer_count', label: 'Total Refer' },
    { key: 'referrals',   label: 'Referrers' },
  ];

  const fundCols = [
    { key: '#',           label: '#',             render: (_r, i) => i + 1 },
    { key: 'name',        label: 'Name',           render: (r) => `${r.fname} ${r.lname}` },
    { key: 'total_funded',label: 'Total Funded',   render: (r) => `₦${fmt(r.total_funded)}` }, // FIX: was "Account"
    { key: 'email',       label: 'Email' },
  ];

  const walletCols = [
    { key: '#',      label: '#',              render: (_r, i) => i + 1 },
    { key: 'name',   label: 'Name',           render: (r) => `${r.fname} ${r.lname}` },
    { key: 'balance',label: 'Wallet Balance', render: (r) => `₦${fmt(r.balance)}` },
    { key: 'email',  label: 'Email' },
  ];

  const salesCols = [
    { key: '#',          label: '#',           render: (_r, i) => i + 1 },
    { key: 'name',       label: 'Name',        render: (r) => `${r.fname} ${r.lname}` },
    { key: 'total_spent',label: 'Total Spent', render: (r) => `₦${fmt(r.total_spent)}` }, // FIX: was "Account"
    { key: 'email',      label: 'Email' },
  ];

  return (
    <div>
      <SectionHeader
        title="User Stats"
        subtitle="Referral contests, top funders, wallet balances and sales breakdowns"
      />

      {/* Monthly Referral Contest */}
      <Section title="Monthly Referral Contest" icon="fa-trophy" color="#f59e0b">
        <div style={{ marginBottom: 12 }}>
          <AdminSearch value={contestSearch} onChange={(e) => setContestSearch(e.target.value)} placeholder="Search email…" />
        </div>
        <StatsTable
          columns={referralCols}
          rows={filterByEmail(contest, contestSearch)}
          loading={contestLoad}
          emptyMsg="No referral contest data yet."
        />
      </Section>

      {/* Refer Fund */}
      <Section title="Refer Fund" icon="fa-hand-holding-dollar" color="#10b981">
        <div style={{ marginBottom: 12 }}>
          <AdminSearch value={referSearch} onChange={(e) => setReferSearch(e.target.value)} placeholder="Search email…" />
        </div>
        <StatsTable
          columns={referralCols}
          rows={filterByEmail(referFund, referSearch)}
          loading={referLoad}
          emptyMsg="No refer fund data yet."
        />
      </Section>

      {/* Top 10 Users */}
      <Section title="Top 10 Users" icon="fa-users" color="#4f46e5">
        <div style={{ marginBottom: 12 }}>
          <AdminSearch value={topUsersSearch} onChange={(e) => setTopUsersSearch(e.target.value)} placeholder="Search name or email…" />
        </div>
        <StatsTable
          columns={fundCols}
          rows={filterByName(topUsers, topUsersSearch)}
          loading={topUsersLoad}
          emptyMsg="No top users data yet."
        />
      </Section>

      {/* Top 10 Wallet Users */}
      <Section title="Top 10 Wallet Users" icon="fa-wallet" color="#0ea5e9">
        <div style={{ marginBottom: 12 }}>
          <AdminSearch value={walletSearch} onChange={(e) => setWalletSearch(e.target.value)} placeholder="Search name or email…" />
        </div>
        <StatsTable
          columns={walletCols}
          rows={filterByName(wallet, walletSearch)}
          loading={walletLoad}
          emptyMsg="No wallet data yet."
        />
      </Section>

      {/* Sales by User */}
      <Section title="Sales by User" icon="fa-chart-bar" color="#ef4444">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-light)', display: 'block', marginBottom: 4 }}>Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-dark)' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-light)', display: 'block', marginBottom: 4 }}>Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-dark)' }}
            />
          </div>
          <button
            onClick={() => loadSales(dateFrom, dateTo)}
            style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <i className="fas fa-search" /> Search
          </button>
          <div style={{ marginLeft: 'auto' }}>
            {/* FIX: uses dedicated handler that filters from full dataset */}
            <AdminSearch value={salesSearch} onChange={handleSalesSearch} placeholder="Search name or email…" />
          </div>
        </div>
        <StatsTable
          columns={salesCols}
          rows={sales}
          loading={salesLoad}
          emptyMsg="No sales data for selected period."
        />
      </Section>
    </div>
  );
}